import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormLabel,
  Input,
  Flex,
  Box,
  Button,
  Text,
  Select,
  useToast,
} from "components-lib";
import axiosInstance from '../../../utils/axios';
import { humanizeApiError } from '../../../utils/api_error_samples';
import dayjs from 'dayjs';

const AddFrontdeskModal = ({
  isOpen,
  onClose,
  frontdeskForm,
  setFrontdeskForm,
  handleAddFrontdesk,
  frontdeskLoading,
  onFrontdeskCreated,
  preselectedClinicId = null, // Optional: if provided by parent, clinic is auto-selected
}) => {
  const [clinicOptions, setClinicOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const toast = useToast();
  const [updateResponse, setUpdateResponse] = useState(false);
  useEffect(() => {
    if (isOpen) {
      // Fetch clinics when modal opens
      axiosInstance.get('api/clinic/allClinics')
        .then(res => {
          console.log('Raw clinics API response:', res.data);
          let clinics = res.data;

          // Handle different response structures
          if (Array.isArray(clinics)) {
            // If it's an array, process each item
            clinics = clinics.map(clinic => ({
              id: clinic.id || clinic._id || clinic.clinic_id,
              name: clinic.name || clinic.clinic_name
            }));
          } else if (clinics && clinics.clinics) {
            // If it has a clinics property, use that array
            clinics = clinics.clinics.map(clinic => ({
              id: clinic.id || clinic._id || clinic.clinic_id,
              name: clinic.name || clinic.clinic_name
            }));
          } else if (clinics && typeof clinics === 'object') {
            // If it's a single object
            clinics = [{
              id: clinics.id || clinics._id || clinics.clinic_id,
              name: clinics.name || clinics.clinic_name
            }];
          } else {
            console.warn('Unexpected clinics data structure:', clinics);
            clinics = [];
          }

          console.log('Processed clinics for dropdown:', clinics);
          setClinicOptions(clinics);

          // If a clinic was preselected and not yet set in form, set it now
          if (preselectedClinicId && !frontdeskForm.clinic_id) {
            setFrontdeskForm((prev) => ({
              ...prev,
              clinic_id: preselectedClinicId,
            }));
          }
        })
        .catch(err => {
          console.error('Error fetching clinics:', err);
          console.error('Error details:', {
            message: err.message,
            response: err.response?.data,
            status: err.response?.status
          });
        });
    }
  }, [isOpen, preselectedClinicId, frontdeskForm.clinic_id, setFrontdeskForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFrontdeskForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validation
    const newErrors = {};
    if (!frontdeskForm.firstname) newErrors.firstname = 'First name is required';
    if (!frontdeskForm.email) newErrors.email = 'Email is required';
    if (!frontdeskForm.user_password) newErrors.user_password = 'Password is required';
    if (!frontdeskForm.phoneno) newErrors.phoneno = 'Phone number is required';
    if (!frontdeskForm.clinic_id) newErrors.clinic_id = 'Clinic is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Prepare registration date
    let regdate = frontdeskForm.registration_date;
    if (!regdate) {
      regdate = dayjs().format('YYYY-MM-DD HH:mm:ss');
    } else {
      regdate = dayjs(frontdeskForm.registration_date).format('YYYY-MM-DD HH:mm:ss');
    }

    // Prepare request body
    const body = {
      firstname: frontdeskForm.firstname,
      lastname: frontdeskForm.lastname,
      email: frontdeskForm.email,
      user_password: frontdeskForm.user_password,
      phoneno: frontdeskForm.phoneno,
      regdate,
      whatsapp_no: frontdeskForm.whatsapp_no,
      clinic_id: Number(frontdeskForm.clinic_id) || undefined,
    };
    console.log('Frontdesk registration request body:', body);
    try {
      const response = await axiosInstance.post('api/frontdesk/register', body);

      if (response.status > 200) {
      setUpdateResponse(response)}
      console.log('Frontdesk registration response:', response.data);
      toast({
        title: "Success",
        description: "Frontdesk registered successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      if (typeof onFrontdeskCreated === 'function') {
        onFrontdeskCreated(response.data);
      }
      onClose();
    } catch (error) {
      console.error('Frontdesk registration error:', error);
      const serverMessage = updateResponse?.data?.error || error.response?.data?.error || error.response?.data?.message || error.message;
      const { message, field } = humanizeApiError('/api/frontdesk', serverMessage);
      if (field) {
        setErrors(prev => ({ ...prev, [field]: message }));
      }
      toast({
        title: "Error",
        description: message || "Failed to register frontdesk",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader>Add Frontdesk</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={3}>
            <Flex gap={2}>
              <Box flex="1">
                <FormLabel>Clinic <span style={{ color: 'red' }}>*</span></FormLabel>
                <Select
                  name="clinic_id"
                  value={frontdeskForm.clinic_id || ''}
                  onChange={handleInputChange}
                  placeholder="Select Clinic"
                  isInvalid={!!errors.clinic_id}
                >
                  {clinicOptions.map(clinic => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </Select>
                {errors.clinic_id && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.clinic_id}
                  </Text>
                )}
              </Box>
            </Flex>
            <Flex gap={2}>
              <Box flex="1">
                <FormLabel>First Name <span style={{ color: 'red' }}>*</span></FormLabel>
                <Input name="firstname" value={frontdeskForm.firstname} onChange={handleInputChange} isRequired />
                {errors.firstname && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.firstname}
                  </Text>
                )}
              </Box>
              <Box flex="1">
                <FormLabel>Last Name</FormLabel>
                <Input name="lastname" value={frontdeskForm.lastname} onChange={handleInputChange} />
              </Box>
            </Flex>
            <Flex gap={2}>
              <Box flex="1">
                <FormLabel>Email <span style={{ color: 'red' }}>*</span></FormLabel>
                <Input name="email" value={frontdeskForm.email} onChange={handleInputChange} type="email" isRequired />
                {errors.email && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.email}
                  </Text>
                )}
              </Box>
              <Box flex="1">
                <FormLabel>Password <span style={{ color: 'red' }}>*</span></FormLabel>
                <Input name="user_password" value={frontdeskForm.user_password} onChange={handleInputChange} type="password" isRequired />
                {errors.user_password && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.user_password}
                  </Text>
                )}
              </Box>
            </Flex>
            <Flex gap={2}>
              <Box flex="1">
                <FormLabel>Phone Number <span style={{ color: 'red' }}>*</span></FormLabel>
                <Input name="phoneno" value={frontdeskForm.phoneno} onChange={handleInputChange} isRequired />
                {errors.phoneno && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.phoneno}
                  </Text>
                )}
              </Box>
              <Box flex="1">
                <FormLabel>WhatsApp Number</FormLabel>
                <Input name="whatsapp_no" value={frontdeskForm.whatsapp_no} onChange={handleInputChange} />
              </Box>
            </Flex>
            <Flex gap={2}>
              <Box flex="1">
                <FormLabel>Registration Date</FormLabel>
                <Input
                  name="registration_date"
                  type="date"
                  value={frontdeskForm.registration_date || ""}
                  onChange={handleInputChange}
                />
              </Box>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={frontdeskLoading}>
            Add Frontdesk
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddFrontdeskModal; 